/**
 * Modal component for creating and editing share links
 */

// import primary libraries
import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// import third-party libraries
import _cloneDeep from 'lodash/cloneDeep'
import classNames from 'classnames'

// import actions
import * as shareLinkActions from '../../shareLinkActions.js'
import * as quickTaskActions from '../../../quickTask/quickTaskActions.js'
import * as userActions from '../../../user/userActions.js'
import * as clientUserActions from '../../../clientUser/clientUserActions.js'

// import global components
import Modal from '../../../../global/components/modals/Modal.jsx'

// import other components
import displayUtils from '../../../../global/utils/displayUtils.js'
import sortUtils from '../../../../global/utils/sortUtils.js'
import htmlSanitizeUtils from '../../../../global/utils/htmlSanitizeUtils.js'
import { getShareAuthTypes } from '../../../../global/utils/getShareAuthTypes.js'
import {
  useGetRequestTemplateQuery,
  useGetRequestTemplatesQuery,
} from '../../../../config/api/requestTemplate/requestTemplate.js'
import { RequestFilesFolderSelection } from '../../components/RequestFilesFolderSelection.jsx'
import { SelectFromObject } from '../../../../global/components/forms/index.js'

import { useGetFirmSettingsQuery } from '../../../../config/api/firm/firm.js'
import { RequestFilesLinkSettings } from '../../components/RequestFilesLinkSettings.jsx'
import { useRequestFilesForm } from '../../hooks/useRequestFilesForm.js'
import { RequestFilesTaskSettings } from '../../components/RequestFilesTaskSettings.jsx'
import * as requestTaskActions from '../../../requestTask/requestTaskActions.js'
import apiUtils from '../../../../global/utils/api.js'
import { useToast } from '../../../../global/hooks/useToast.jsx'
import { CircularProgress } from '@mui/material'
import FileLocation from '../../../file/components/FileLocation.jsx'

const ShareRequestFilesModal = ({
  client = null,
  clientStore,
  clientUserStore,
  userStore,
  staffStore,
  shareLinkStore,
  workspaceList,
  loggedInUser,
  firm,
  isOpen,
  close,
  dispatch,
  listArgs,
  handleSetInvalidList,
  match,
  clientUserListItems,
  handleUpdateList,
}) => {
  const params = match?.params || {}
  const linkInputRef = useRef(null)
  const firmId = match.params.firmId
  const [activeTab, setActiveTab] = useState('default')

  // Initialize auth types
  const { authTypes: initialAuthTypes, isAuthTypeRestricted } =
    getShareAuthTypes(staffStore, loggedInUser, firm)

  const getDefaultAuthType = () => {
    return firm.authDefault === 'QA' ||
      !initialAuthTypes.find(type => type.val === 'none')
      ? 'secret-question'
      : 'none'
  }

  // Use the reducer hook
  const { state, actions } = useRequestFilesForm()

  const { data: firmSettings, isLoading: isLoadingFirmSettings } =
    useGetFirmSettingsQuery(firmId)

  const hasRequestTemplateFeature =
    firmSettings?.featureFlags?.REQUEST_TEMPLATES

  const {
    data: { data: requestTemplatesData } = {},
    isLoading: isRequestTemplatesLoading,
  } = useGetRequestTemplatesQuery(firmId, {
    skip: !firmId || !hasRequestTemplateFeature,
  })

  const { data: requestTemplateData, isLoading: isRequestTemplateLoading } =
    useGetRequestTemplateQuery(
      {
        firmId: parseInt(firmId),
        requestTemplateId: parseInt(state.selectedRequestTemplate?.id),
      },
      {
        skip: !firmId || !state.selectedRequestTemplate?.id,
      },
    )

  useEffect(() => {
    if (requestTemplateData?.data) {
      const template = requestTemplateData.data

      // Set template documents
      if (template.documents && Array.isArray(template.documents)) {
        actions.setTemplateDocuments(template.documents)
      }

      // Update the selected template with full data if needed
      if (template.settings) {
        actions.setRequestTemplate({
          ...state.selectedRequestTemplate,
          settings: template.settings,
          documents: template.documents,
        })
      }
    }
  }, [requestTemplateData])

  // Initialize secret questions on mount
  useEffect(() => {
    if (firm && firm.secretQuestions) {
      const cusSecretQuestions =
        typeof firm.secretQuestions === 'string'
          ? JSON.parse(firm.secretQuestions)
          : firm.secretQuestions

      const other = {
        other: { display: 'Other', val: 'other', prompt: '' },
      }

      if (Object.entries(cusSecretQuestions).length > 0) {
        actions.setSecretQuestions({ ...cusSecretQuestions, ...other })
      } else {
        actions.setSecretQuestions(other)
      }
    } else {
      const other = {
        other: { display: 'Other', val: 'other', prompt: '' },
      }
      actions.setSecretQuestions(other)
    }
  }, [firm])

  // Initialize form with default values
  useEffect(() => {
    actions.initializeForm({
      authTypes: initialAuthTypes,
      authType: getDefaultAuthType(),
      clientId: client ? client._id : null,
      _personal: params.userId ? `personal${params.userId}` : null,
      selectedFolder: {
        _client: params.clientId ? params.clientId : null,
        _personal: params.userId ? params.userId : '',
        _id: params.folderId ? params.folderId : '',
      },
    })
  }, [])

  // Handle client changes
  useEffect(() => {
    if (client) {
      handleClientChange({
        target: {
          value: client._id,
          name: 'clientId',
        },
      })
    }
  }, [client])

  // The shared toast (ToastProvider in app.jsx and outlook.jsx). This modal
  // reports mostly failures, so its default severity is an error.
  const toast = useToast()
  const showToast = (message, type = 'error') => toast(message, type)

  const copyToClipboard = () => {
    if (linkInputRef.current) {
      linkInputRef.current.select()
      document.execCommand('copy')
      actions.setCopySuccess(true)
    }
  }

  const handleClose = () => {
    dispatch(shareLinkActions.invalidateSelected())
    actions.resetForm(params, client, getDefaultAuthType())
    close()
  }

  const getFilteredClientUsers = () => {
    if (!state.clientId) {
      return []
    }

    const clientUsers = Object.values(clientUserStore.byId).filter(
      cu => Number(cu._client) === Number(state.clientId),
    )

    const userIds = clientUsers.map(cu => cu._user)
    return clientUserListItems.filter(user => userIds.includes(user._id))
  }

  const formatDate = date => {
    if (!date) return null
    const d = new Date(date)
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
  }

  const handleSubmit = () => {
    if (activeTab === 'with-template') {
      handleCreateTask()
    } else {
      handleCreateShareLink()
    }
  }

  const handleCreateTask = async () => {
    const hasTemplate = state.selectedRequestTemplate
    const hasTemplateDocuments =
      state.templateDocuments && state.templateDocuments.length > 0
    const hasSelectedDocuments = state.selectedDocuments.length > 0
    const hasOtherInputs = state.otherInputs.some(
      input => input && input.trim(),
    )

    if (!hasTemplate) {
      showToast('Please select a type of engagement', 'error')
      return
    }

    if (hasTemplateDocuments && !hasSelectedDocuments && !hasOtherInputs) {
      showToast(
        'Please select at least one document or add custom documents',
        'error',
      )
      return
    }

    if (
      !hasTemplateDocuments &&
      state.selectedRequestTemplate.settings.other_input_enabled &&
      !hasOtherInputs
    ) {
      showToast('Please add at least one custom document', 'error')
      return
    }
    actions.setSubmitting(true)

    const apiPayload = {
      // Core fields
      dueDate: state.dueDate,
      assignee: [],
      category: state.selectedRequestTemplate.name,
      description: state.selectedRequestTemplate.name,
      _request: 'default',
      _firm: firmId,
      _client: selectedClient?._id || null,
    }

    apiPayload.request_template_id = state.selectedRequestTemplate.id
    apiPayload.special_instructions = state.specialInstructions

    if (state.selectedRequestTemplate.settings.date_range_enabled) {
      apiPayload.documents_range_from_date = formatDate(state.dateRange.from)
      apiPayload.documents_range_to_date = formatDate(state.dateRange.to)
    }

    const documents = []

    state.selectedDocuments.forEach(docId => {
      documents.push({
        request_template_document_id: docId,
        custom_name: null,
        range_from_date:
          formatDate(state.perDocumentDates[docId]?.from) || null,
        range_to_date: formatDate(state.perDocumentDates[docId]?.to) || null,
      })
    })

    if (
      state.selectedRequestTemplate.settings.other_input_enabled &&
      state.otherInputs
    ) {
      state.otherInputs.forEach((input, index) => {
        if (input && input.trim()) {
          documents.push({
            request_template_document_id: null,
            custom_name: input.trim(),
            range_from_date:
              formatDate(state.perOtherInputDates[index]?.from) || null,
            range_to_date:
              formatDate(state.perOtherInputDates[index]?.to) || null,
          })
        }
      })
    }

    apiPayload.documents = documents

    if (state.assignees && state.assignees.length) {
      state.assignees.forEach(({ emailError, ...item }) => {
        if (item && item.firstname && item.lastname && item.username) {
          apiPayload.assignee.push(item)
        }
      })
    }

    try {
      const json = await dispatch(
        requestTaskActions.sendCreateRequestTask(apiPayload),
      )

      if (json.success) {
        // Update status to published
        const taskId = String(json.id)
        const requestId = String(json.request._id)

        if (taskId && requestId) {
          const statusRequestBody = {
            firmId: firmId,
            requestListId: requestId,
            requestTaskIds: [taskId],
            status: 'published',
            _personal: state.selectedFolder._personal,
            createFolderStructure: true,
          }

          try {
            const statusJson = await apiUtils.callAPI(
              '/api/request-task-bulk-update-status',
              'PUT',
              statusRequestBody,
            )

            if (statusJson.success) {
              showToast('Request task created successfully!', 'success')
            } else {
              console.error('Failed to update status to published')
              showToast('Task created but status update failed', 'warning')
            }
            handleSetInvalidList()
            handleUpdateList()
          } catch (statusError) {
            console.error('Error updating status:', statusError)
            showToast('Task created but status update failed', 'warning')
          }
        }

        setTimeout(() => {
          actions.setSubmitting(false)
          actions.resetForm(params, client, getDefaultAuthType())
          handleClose()
        }, 1500)
      } else {
        actions.setSubmitting(false)
        showToast(json.error || 'Failed to create request task', 'error')
      }
    } catch (error) {
      actions.setSubmitting(false)
      console.error('Error creating task:', error)
      showToast('Failed to create request task. Please try again.', 'error')
    }
  }

  const handleCreateShareLink = () => {
    if (state.authType === 'secret-question') {
      if (!state.selectedQuestion || !state.password) {
        showToast('There was a problem creating the share link.', 'error')
        return
      }
    }

    actions.setSubmitting(true)

    const shareLinkPassword =
      state.authType === 'shared-client-secret'
        ? clientStore.byId[state.clientId]?.sharedSecretAnswer || ''
        : state.authType === 'shared-contact-secret'
          ? userStore.byId[state.userId]?.sharedSecretAnswer || ''
          : state.password

    const shareLinkPrompt =
      state.authType === 'shared-client-secret'
        ? clientStore.byId[state.clientId]?.sharedSecretPrompt || ''
        : state.authType === 'shared-contact-secret'
          ? userStore.byId[state.userId]?.sharedSecretPrompt || ''
          : state.authType === 'secret-question'
            ? state.secretQuestions[state.selectedQuestion]?.prompt
            : state.prompt

    const newShareLink = {
      _client: state.clientId,
      _firm: firm._id,
      emailMessage: state.emailMessage,
      sentTo: state.recipients
        .filter(user => !!user)
        .map(({ emailError, ...r }) => r),
      authType: state.authType,
      expireDate: state.expires ? new Date(state.expireDate) : null,
      password: shareLinkPassword,
      prompt: shareLinkPrompt,
      type: 'file-request',
      sN_upload: state.sN_upload,
    }

    if (state._personal) {
      newShareLink._personal = state._personal.replace('personal', '')
    }

    let mangoSubFolder = null

    if (state.selectedFolder && state.selectedFolder._id) {
      newShareLink._folder = state.selectedFolder._id
    }

    newShareLink.ParentID =
      mangoSubFolder && mangoSubFolder.DMSParentID
        ? mangoSubFolder.DMSParentID
        : null
    newShareLink.YellowParentID =
      mangoSubFolder && mangoSubFolder.DMSParentID
        ? mangoSubFolder.DMSParentID
        : null

    dispatch(shareLinkActions.sendCreateShareLink(newShareLink)).then(slRes => {
      const shareLink = slRes.item
      if (slRes.success) {
        actions.setEmailResults({
          [shareLink._id + '_emailResults']: shareLink.emailResults,
        })

        const quickTask = {
          _client: state.clientId,
          _firm: firm._id,
          type: 'file',
          prompt: state.instructions,
          selectedStaff: state.selectedStaff,
        }

        if (state.receiveEmails) {
          quickTask['signingLinks'] = {
            action: 'request',
            sentTo: state.receivers
              .filter(email => email)
              .map(({ emailError, ...r }) => r),
          }
        }

        dispatch(quickTaskActions.sendCreateQuickTask(quickTask)).then(
          taskRes => {
            let newReceivers = _cloneDeep(state.receivers)
            let receivers = []

            if (newReceivers.length && taskRes.item.signingLinks) {
              if (taskRes.item.signingLinks.action === 'request') {
                receivers = taskRes.item.signingLinks.sentTo
              }
            }

            if (taskRes.success) {
              shareLink._quickTask = taskRes.item._id
              dispatch(
                shareLinkActions.sendUpdateShareLinkWithPermission(shareLink),
              ).then(shareLinkRes => {
                actions.setAuthType(
                  state.authTypes.find(type => type.val === 'none')
                    ? 'none'
                    : 'secret-question',
                )
                actions.setField('password', '')
                actions.setField('prompt', '')
                actions.setSubmitting(false)
                actions.setField('receivers', receivers)
                actions.setField('selectedQuestion', 'dssn')
                actions.setField('selectedStaff', '')

                if (shareLinkRes.success) {
                  showToast('The request was created successfully!', 'success')
                } else {
                  showToast(
                    'There was a problem updating the share link.',
                    'error',
                  )
                }
              })
            } else {
              showToast('There was a problem creating the quick task.', 'error')
            }
          },
        )
      } else {
        showToast('There was a problem creating the share link.', 'error')
      }
    })
  }

  const handleLocationChange = folder => {
    actions.setSelectedFolder(folder)
  }

  const getRecipientList = () => {
    const userListItems = userStore.util.getList(
      '_client',
      state.clientId || client?._id,
    )
    const recipientList = userListItems
      ? userListItems.map(user => ({
          displayName: `${user.firstname} ${user.lastname}`,
          email: user.username,
        }))
      : []
    return recipientList
  }

  const getAllRecipientList = () => {
    const idLists = Object.keys(userStore.byId)
    const recipientList = idLists
      ? idLists.map(id => {
          const user = userStore.byId[id]
          return {
            displayName: `${user.firstname} ${user.lastname}`,
            email: user.username,
          }
        })
      : []
    return recipientList
  }

  const handleClientChange = e => {
    const val = e.target.value ? e.target.value.toString() : ''
    const personalId = val.includes('personal') ? val : null
    const newClientId = val.includes('personal') ? null : val ? val : null

    if (newClientId) {
      dispatch(userActions.fetchListIfNeeded('_client', newClientId))
      dispatch(clientUserActions.fetchListIfNeeded('_client', newClientId))
    }

    let newAuthTypes = [...initialAuthTypes]

    const selectedClient = clientStore.byId[newClientId]

    if (e.target.name === 'clientId') {
      const newFolder = {
        _id: 'rootfolder',
        filename: 'Root Folder',
      }
      if (val.includes('personal')) {
        newFolder._client = null
        newFolder._personal = val.replace('personal', '')
        newFolder._id = ''
      } else if (val === 'public') {
        newFolder._client = ''
        newFolder._personal = ''
        newFolder._id = ''
      } else {
        newFolder._client = val
        newFolder._personal = ''
        newFolder._id = ''
      }
      actions.setSelectedFolder(newFolder)
    }

    if (selectedClient) {
      if (selectedClient.sharedSecretPrompt) {
        newAuthTypes.push({
          display: `${selectedClient.name} - Secret Question`,
          val: 'shared-client-secret',
        })
      }

      dispatch(
        clientUserActions.fetchListIfNeeded('_client', newClientId),
      ).then(cuRes => {
        if (cuRes.success) {
          const filteredUserList = cuRes.list.flatMap(cu => {
            if (
              cu &&
              cu.status === 'active' &&
              userStore.byId[cu._user] &&
              userStore.byId[cu._user].sharedSecretPrompt
            ) {
              return userStore.byId[cu._user]
            }
            return []
          })

          const userSecretList = filteredUserList
            ? filteredUserList.map(user => ({
                display: `${user.firstname} ${user.lastname} - shared secret question`,
                val: user._id,
              }))
            : []

          if (userSecretList && userSecretList.length > 0) {
            const updatedAuthTypes = [
              ...newAuthTypes,
              {
                display: "Specific Contact's Secret Question",
                val: 'shared-contact-secret',
              },
            ]
            actions.setClient(
              newClientId,
              personalId,
              updatedAuthTypes,
              userSecretList,
            )
          } else {
            actions.setClient(newClientId, personalId, newAuthTypes, null)
          }
        }
      })
    } else {
      actions.setClient(newClientId, personalId, newAuthTypes, null)
    }
  }

  const handleRTEChange = value => {
    actions.setField('instructions', htmlSanitizeUtils.sanitizeString(value))
  }

  const handleRequestTemplateChange = e => {
    const templateId = e.target.value

    const selectedTemplate = requestTemplatesData.find(
      template => Number(template.id) === Number(templateId),
    )

    actions.setRequestTemplate(selectedTemplate || null)
  }

  // Computed values
  const selectedClient = state.clientId
    ? clientStore.byId[state.clientId]
    : null
  const selectedShareLink = shareLinkStore.selected.getItem()

  let recipientListItems = selectedClient
    ? getRecipientList()
    : getAllRecipientList()
  recipientListItems = sortUtils._object(recipientListItems, 'displayName')

  const linkEmpty =
    !selectedShareLink ||
    !selectedShareLink._id ||
    shareLinkStore.selected.didInvalidate
  const linkFetching =
    shareLinkStore.selected.isFetching ||
    isRequestTemplatesLoading ||
    isLoadingFirmSettings

  const linkClass = classNames('-copyable-share-link', {
    '-visible': state.copySuccess,
  })

  const promptClass = classNames('-prompt', {
    '-hidden': state.copySuccess,
  })

  const staffListItems = staffStore.util.getList('_firm', params.firmId)
  const availableStaff = !staffListItems
    ? []
    : staffListItems.filter(staff => {
        if (staff.status === 'active') {
          const item = staff
          const fullName = userStore.byId[staff._user]
            ? `${userStore.byId[staff._user].firstname} ${userStore.byId[staff._user].lastname}`
            : ''
          const userName = userStore.byId[staff._user]
            ? userStore.byId[staff._user].username
            : ''
          item.displayName = `${fullName} | ${userName}`
          item.email = userName
          return item
        }
      })

  const hasInvalidEmails =
    state.recipients.some(recipient => recipient && recipient.emailError) ||
    state.receivers.some(r => r && r.emailError)

  return (
    <>
      <Modal
        cardSize="large"
        closeAction={handleClose}
        closeText="Cancel"
        modalClasses="share-files"
        confirmAction={handleSubmit}
        confirmText={
          state.submitting ? 'Creating...' : 'Create request files link'
        }
        disableConfirm={state.submitting || hasInvalidEmails}
        isOpen={isOpen}
        modalHeader={
          selectedClient
            ? `Request files from ${selectedClient.name}`
            : 'Request files'
        }
        showButtons={linkEmpty}
      >
        {linkEmpty ? (
          linkFetching ? (
            <div
              style={{
                height: '800px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div>
              <div className="-share-link-configuration">
                <div className="-header">
                  <i className="fas fa-eye" /> Link settings
                </div>
                <div className="-body">
                  {/* WORKSPACE SELECTION - Always visible */}
                  <RequestFilesFolderSelection
                    workspaceList={workspaceList}
                    client={client}
                    clientId={state.clientId}
                    _personal={state._personal}
                    handleClientChange={handleClientChange}
                  />
                  {/* Tabs: only visible if templates feature/availability condition is true */}
                  {hasRequestTemplateFeature &&
                    requestTemplatesData?.length > 0 && (
                      <div
                        style={{
                          marginBottom: '16px',
                        }}
                      >
                        <div className="tab-bar-nav">
                          <ul className="navigation">
                            <li>
                              <span
                                className={`action-link ${
                                  activeTab === 'default' ? 'active' : ''
                                }`}
                                onClick={() => setActiveTab('default')}
                              >
                                Default
                              </span>
                            </li>
                            <li>
                              <span
                                className={`action-link ${
                                  activeTab === 'with-template' ? 'active' : ''
                                }`}
                                onClick={() => setActiveTab('with-template')}
                              >
                                With Template
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                  {activeTab === 'default' && (
                    <div>
                      {/* Folder selector inside Default tab */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '16px',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{ width: '50%', marginBottom: '16px' }}
                        ></div>
                        <div style={{ width: '50%' }}>
                          <FileLocation
                            selectedClient={selectedClient}
                            handleLocationChange={handleLocationChange}
                            listArgs={listArgs}
                            allowCreateFolder={true}
                            handleSetInvalidList={handleSetInvalidList}
                            selectedFolder={state.selectedFolder}
                            personalId={
                              state._personal
                                ? state._personal.replace('personal', '')
                                : null
                            }
                            getDetail={{
                              type:
                                (state.clientId && 'workspace') ||
                                (state._personal && 'personal') ||
                                'general',
                              id: state.clientId || state._personal,
                              firmId: params.firmId,
                            }}
                          />
                        </div>
                      </div>

                      {/* Default content (no template selected) */}
                      <RequestFilesLinkSettings
                        authType={state.authType}
                        authTypes={state.authTypes}
                        isAuthTypeRestricted={isAuthTypeRestricted}
                        secretQuestions={state.secretQuestions}
                        selectedQuestion={state.selectedQuestion}
                        password={state.password}
                        userSecretQuestionList={state.userSecretQuestionList}
                        userId={state.userId}
                        sN_upload={state.sN_upload}
                        expires={state.expires}
                        expireDate={state.expireDate}
                        addInstructions={state.addInstructions}
                        instructions={state.instructions}
                        handleRTEChange={handleRTEChange}
                        sendEmails={state.sendEmails}
                        recipients={state.recipients}
                        recipientListItems={recipientListItems}
                        emailMessage={state.emailMessage}
                        receiveEmails={state.receiveEmails}
                        receivers={state.receivers}
                        availableStaff={availableStaff}
                        actions={actions}
                      />
                    </div>
                  )}

                  {activeTab === 'with-template' && (
                    <div>
                      {/* Template selector inside With Template tab */}
                      <div
                        className="yt-row"
                        style={{ marginBottom: '16px' }}
                      >
                        <div className="yt-col _50">
                          <p>
                            <strong>Select Type of Engagement</strong>
                          </p>
                        </div>
                        <div className="yt-col _50">
                          <SelectFromObject
                            change={handleRequestTemplateChange}
                            name="requestTemplate"
                            placeholder="-- Select Template --"
                            items={requestTemplatesData}
                            display="name"
                            value="id"
                            selected={state.selectedRequestTemplate?.id || ''}
                            isClearable={true}
                          />
                        </div>
                      </div>

                      {/* Content when a request template is selected */}
                      {state.selectedRequestTemplate && (
                        <RequestFilesTaskSettings
                          selectedRequestTemplate={
                            state.selectedRequestTemplate
                          }
                          templateDocuments={state.templateDocuments}
                          selectedDocuments={state.selectedDocuments}
                          specialInstructions={state.specialInstructions}
                          dateRange={state.dateRange}
                          otherInputs={state.otherInputs}
                          perDocumentDates={state.perDocumentDates}
                          perOtherInputDates={state.perOtherInputDates}
                          dueDate={state.dueDate}
                          assignees={state.assignees}
                          clientUserListItems={getFilteredClientUsers()}
                          selectedClient={selectedClient}
                          actions={actions}
                          match={match}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ) : (
          <div>
            <h4>Request files link created</h4>
            <div className="yt-row -share-link-row center-vert">
              <div className="-icon">
                <i className="fas fa-eye fa-lg" />
              </div>
              <div className="-description">
                <div className={promptClass}>
                  <p>
                    <strong>Can view</strong>
                    <br />
                  </p>
                  <p>
                    {displayUtils.getShareLinkViewParams(
                      selectedShareLink.authType,
                    )}
                  </p>
                </div>
                <div className={linkClass}>
                  <input
                    ref={linkInputRef}
                    value={selectedShareLink.url}
                    readOnly={true}
                  />
                </div>
              </div>
              <div className="-copy-action">
                {state.copySuccess ? (
                  <button
                    type="button"
                    className="yt-btn x-small link info"
                    onClick={() => actions.setCopySuccess(false)}
                  >
                    Hide link
                  </button>
                ) : (
                  <button
                    type="button"
                    className="yt-btn x-small link info"
                    onClick={copyToClipboard}
                  >
                    Copy link
                  </button>
                )}
              </div>
            </div>
            {selectedShareLink.sentTo &&
              selectedShareLink.sentTo.length > 0 &&
              selectedShareLink.sentTo[0].email && (
                <div>
                  <h4>Users notified</h4>
                  {state.emailResults[
                    selectedShareLink._id + '_emailResults'
                  ] ? (
                    state.emailResults[
                      selectedShareLink._id + '_emailResults'
                    ].map((result, i) => (
                      <div
                        key={'email_result_' + i}
                        className="yt-row -share-link-row center-vert"
                      >
                        <div className="yt-col">
                          <small>{`${result.email}`}</small>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="loading -small"></div>
                  )}
                </div>
              )}
            {state.receiveEmails &&
              state.receivers &&
              state.receivers[0]?.email && (
                <div>
                  <h4>Users notified when contacts uploaded a file</h4>
                  {state.receivers.map((result, i) => (
                    <div
                      key={'email_result_' + i}
                      className="yt-row -share-link-row center-vert"
                    >
                      <div className="yt-col">
                        <small>{`${result.email}`}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </Modal>
    </>
  )
}

ShareRequestFilesModal.propTypes = {
  client: PropTypes.object,
  close: PropTypes.func.isRequired,
  firm: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
}

const mapStoreToProps = (store, props) => {
  const { firm, clientListItem, match } = props
  const loggedInUser = store.user.loggedIn.user
  let workspaceList = clientListItem

  if (workspaceList) {
    workspaceList = workspaceList.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1
      } else {
        return 1
      }
    })
  }

  if (workspaceList && loggedInUser) {
    workspaceList.unshift({
      _id: `personal${loggedInUser._id}`,
      name: 'Your Staff Files',
      _firm: firm._id,
      _staff: loggedInUser._id,
    })
  }

  const userId = match?.params?.userId
  if (
    workspaceList &&
    loggedInUser &&
    loggedInUser._id &&
    store.user &&
    store.user.byId &&
    userId &&
    userId !== loggedInUser._id &&
    store.user.byId[userId]
  ) {
    workspaceList.unshift({
      _id: `personal${userId}`,
      name: `Personal Files for ${store.user.byId[userId].firstname} ${store.user.byId[userId].lastname}`,
      _firm: firm._id,
      _staff: userId,
    })
  }

  const firmId = Number(match.params.firmId)
  const userList = Object.values(store.user.byId || {})
  const filteredUsers = userList.filter(user => {
    return !(
      user.portal_disabled_firms && user.portal_disabled_firms.includes(firmId)
    )
  })
  const clientUserListItems = filteredUsers.map(user => {
    const displayName =
      !user.firstname && !user.lastname
        ? user.username
        : `${user.firstname} ${user.lastname}`
    return {
      ...user,
      displayName,
      email: user.username,
    }
  })

  return {
    clientStore: store.client,
    clientUserStore: store.clientUser,
    shareLinkStore: store.shareLink,
    userStore: store.user,
    staffStore: store.staff,
    workspaceList,
    loggedInUser,
    match,
    clientUserListItems, // Add this to props
  }
}

export default connect(mapStoreToProps)(ShareRequestFilesModal)
